import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import routerService from '../services/routerService';
import spopService from '../services/spopService';

interface EditRouterDialogProps {
  open: boolean;
  onClose: () => void;
  router: any;
}

interface RouterFormData {
  name: string;
  ip_address: string;
  username: string;
  password: string;
  port: number;
  isActive: boolean;
  description?: string;
  os_version: string;
  spopdId?: string;
}

const EditRouterDialog: React.FC<EditRouterDialogProps> = ({ open, onClose, router }) => {
  const [formData, setFormData] = useState<RouterFormData>({
    name: '',
    ip_address: '',
    username: '',
    password: '',
    port: 8728,
    isActive: true,
    description: '',
    os_version: '6',
    spopdId: '',
  });
  
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');

  const queryClient = useQueryClient();

  // Get SPOPs for dropdown
  const { data: spopsData, isLoading: spopsLoading } = useQuery({
    queryKey: ['spops-all'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data.data.spops;
    },
  });

  // Populate form when router data changes
  useEffect(() => {
    if (router && open) {
      setFormData({
        name: router.name || '',
        ip_address: router.host || router.ip_address || '',
        username: router.username || '',
        password: '', // Don't populate password for security
        port: router.port || 8728,
        isActive: router.status === 'active',
        description: router.description || '',
        os_version: router.os_version || '6',
        spopdId: router.spopdId || router.spop?.id || '',
      });
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  }, [router, open]);

  const updateRouterMutation = useMutation({
    mutationFn: (data: RouterFormData) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        host: data.ip_address,
        username: data.username,
        port: data.port,
        description: data.description,
        os_version: data.os_version,
        isActive: data.isActive,
        spopdId: data.spopdId || undefined,
      };
      if (data.password?.trim()) {
        payload.password = data.password;
      }
      return routerService.updateRouter(router.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routers'] });
      onClose();
      setFormData({
        name: '',
        ip_address: '',
        username: '',
        password: '',
        port: 8728,
        isActive: true,
        description: '',
        os_version: '6',
        spopdId: '',
      });
    },
    onError: (error: any) => {
      console.error('Error updating router:', error);
    },
  });

  const handleInputChange = (field: keyof RouterFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'port' ? parseInt(event.target.value) || 0 
                 : field === 'isActive' ? event.target.checked
                 : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestConnection = async () => {
    if (!formData.ip_address || !formData.username || !formData.password) {
      setConnectionStatus('error');
      setConnectionMessage('Host, username, dan password harus diisi untuk test koneksi');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setConnectionMessage('');

    try {
      const testData = {
        host: formData.ip_address,
        username: formData.username,
        password: formData.password,
        port: formData.port
      };

      const response = await routerService.testConnection(testData);
      
      if (response.success) {
        setConnectionStatus('success');
        setConnectionMessage('Koneksi berhasil! Router dapat diakses.');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(response.message || 'Koneksi gagal');
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setConnectionMessage(error.response?.data?.message || 'Error saat test koneksi');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.ip_address || !formData.username) {
      return;
    }

    updateRouterMutation.mutate(formData);
  };

  const handleClose = () => {
    onClose();
    setFormData({
      name: '',
      ip_address: '',
      username: '',
      password: '',
      port: 8728,
      isActive: true,
      description: '',
      os_version: '6',
      spopdId: '',
    });
    setConnectionStatus('idle');
    setConnectionMessage('');
  };

  if (!router) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h5" component="h2" fontWeight="bold">
          Edit Router
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update informasi router MikroTik
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Nama Router"
              value={formData.name}
              onChange={handleInputChange('name')}
              fullWidth
              required
              placeholder="Contoh: Router-Utama"
            />
            <TextField
              select
              label="Router OS Version"
              fullWidth
              margin="normal"
              value={formData.os_version}
              onChange={handleInputChange('os_version')}
              SelectProps={{ native: true }}
            >
              <option value="6">RouterOS v6</option>
              <option value="7">RouterOS v7</option>
            </TextField>

            <TextField
              label="Host/IP Address"
              value={formData.ip_address}
              onChange={handleInputChange('ip_address')}
              fullWidth
              required
              placeholder="192.168.1.1"
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                fullWidth
                required
                placeholder="admin"
              />

              <TextField
                label="Port"
                type="number"
                value={formData.port}
                onChange={handleInputChange('port')}
                fullWidth
                required
                placeholder="8728"
              />
            </Box>

            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              fullWidth
              placeholder="Kosongkan jika tidak ingin mengubah password"
              helperText="Kosongkan jika tidak ingin mengubah password"
            />

            <TextField
              label="Deskripsi"
              value={formData.description}
              onChange={handleInputChange('description')}
              fullWidth
              multiline
              rows={2}
              placeholder="Deskripsi router (opsional)"
            />

            {/* SPOP Selection */}
            <FormControl fullWidth>
              <InputLabel id="spop-select-label">SPOP</InputLabel>
              <Select
                labelId="spop-select-label"
                value={formData.spopdId || ''}
                onChange={(e) => setFormData({ ...formData, spopdId: e.target.value })}
                label="SPOP"
                disabled={spopsLoading}
              >
                <MenuItem value="">
                  <em>Pilih SPOP</em>
                </MenuItem>
                {spopsData?.map((spop: any) => (
                  <MenuItem key={spop.id} value={spop.id}>
                    {spop.name} - {spop.areaCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleInputChange('isActive')}
                  color="primary"
                />
              }
              label="Router Aktif"
            />

            {/* Test Connection Section */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight="medium">
                  Test Koneksi Router
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !formData.ip_address || !formData.username || !formData.password}
                  startIcon={testingConnection ? <CircularProgress size={16} /> : null}
                >
                  {testingConnection ? 'Testing...' : 'Test Koneksi'}
                </Button>
              </Box>

              {connectionStatus !== 'idle' && (
                <Alert 
                  severity={connectionStatus === 'success' ? 'success' : 'error'}
                  sx={{ mt: 1 }}
                >
                  {connectionMessage}
                </Alert>
              )}

              {connectionStatus === 'idle' && (
                <Typography variant="caption" color="text.secondary">
                  Isi password untuk melakukan test koneksi
                </Typography>
              )}
            </Box>

            {updateRouterMutation.error && (
              <Alert severity="error">
                {(updateRouterMutation.error as any)?.response?.data?.message || 'Error saat mengupdate router'}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose}
            disabled={updateRouterMutation.isPending}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              updateRouterMutation.isPending ||
              !formData.name ||
              !formData.ip_address ||
              !formData.username
            }
            startIcon={updateRouterMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            {updateRouterMutation.isPending ? 'Menyimpan...' : 'Update Router'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditRouterDialog;
